import { useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinSunItem {
  id: string;
  label: string;
}

interface SkinSunProps {
  options?: SkinSunItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

const defaultSkinSunOptions: SkinSunItem[] = [
  { id: "highly-sensitive", label: "Burns very easily, never tans" },
  { id: "often-sensitive", label: "Burns easily, rarely tans" },
  { id: "usually-tan", label: "Burns sometimes, usually tans" },
  { id: "rarely-sensitive", label: "Rarely burns, tans easily" },
  { id: "resilient", label: "Almost never burns, deeply pigmented" },
];

export const SkinSun = ({
  options = defaultSkinSunOptions,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText = "Continue",
  skipText = "Skip for now",
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinSunProps) => {
  const [selected, setSelected] = useState<SkinSunItem | null>(
    initialSelectedId
      ? options.find((o) => o.id === initialSelectedId) ?? null
      : null
  );

  const handleSelectionChange = (item: SkinSunItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {continueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {skipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">
        How does your skin respond to the sun?
      </PersonalisationHeading>

      <SingleChoiceTagPicker
        items={options}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel="Select your skin sun response"
      />
    </PersonalisationScreenLayout>
  );
};
